import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Loader2 } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => (n || 0).toLocaleString();

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#f0f0f3] bg-[#FCFCFE] px-4 py-3">
      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{label}</p>
      <p className="mt-1 text-lg font-semibold text-[#06060C]">{value}</p>
    </div>
  );
}

export default function GoogleAdsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try { const res = await api.get(`/ws/google-ads/report?days=${d}`); setData(res.data); }
    catch { setData(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Hide entirely for workspaces that don't use Google Ads (no Customer ID configured).
  if (!loading && (!data || !data.customer_id)) return null;

  const t = data?.totals || {};
  const campaigns = data?.campaigns || [];

  return (
    <div className="mb-8 rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6" data-testid="google-ads-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-xl bg-[#E8F0FE] text-[#4285F4] flex items-center justify-center"><BarChart3 className="h-4 w-4" /></span>
          <div>
            <p className="font-display text-base font-medium text-[#06060C]">Google Ads performance</p>
            <p className="text-[11px] text-[#9FA1A4]">{data?.last_sync ? `Updated ${new Date(data.last_sync).toLocaleDateString()}` : "Syncs automatically"}</p>
          </div>
        </div>
        <Select value={days} onValueChange={(v) => { setDays(v); load(v); }}>
          <SelectTrigger data-testid="ads-panel-range" className="h-9 w-36 bg-white border-[#EEEFEF] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-[#EEEFEF]">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-8 text-center text-sm text-[#9FA1A4]" data-testid="ads-panel-empty">No campaign data for this period yet. It refreshes automatically each night.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <Stat label="Spend" value={money(t.spend)} />
            <Stat label="Impressions" value={num(t.impressions)} />
            <Stat label="Clicks" value={num(t.clicks)} />
            <Stat label="Conversions" value={(t.conversions || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} />
            <Stat label="CTR" value={`${((t.ctr || 0) * 100).toFixed(2)}%`} />
            <Stat label="Avg. CPC" value={money(t.cpc)} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ads-panel-table">
              <thead><tr className="text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] border-b border-[#f0f0f3]">
                <th className="px-3 py-2">Campaign</th><th className="px-3 py-2 text-right">Spend</th><th className="px-3 py-2 text-right">Impr.</th><th className="px-3 py-2 text-right">Clicks</th><th className="px-3 py-2 text-right">Conv.</th>
              </tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-[#f7f7fa]">
                    <td className="px-3 py-2 text-[#06060C]">{c.campaign_name}</td>
                    <td className="px-3 py-2 text-right text-[#595D62]">{money(c.spend)}</td>
                    <td className="px-3 py-2 text-right text-[#595D62]">{num(c.impressions)}</td>
                    <td className="px-3 py-2 text-right text-[#595D62]">{num(c.clicks)}</td>
                    <td className="px-3 py-2 text-right text-[#595D62]">{(c.conversions || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
